import React from 'react';
import { Search, Star, MessageSquare, RefreshCcw, ArrowLeft } from 'lucide-react';

const getInitials = (name, fallbackId) => {
    if (name && typeof name === 'string' && name.trim()) {
        const words = name.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        if (words[0].length >= 2) {
            return words[0].slice(0, 2).toUpperCase();
        }
        return words[0][0].toUpperCase();
    }
    if (fallbackId && typeof fallbackId === 'string') {
        return fallbackId.slice(-2).toUpperCase();
    }
    return 'CU';
};

export default function ChatSidebar({ 
    conversations = [], 
    activeChat, 
    onSelectChat, 
    filterType, 
    setFilterType, 
    searchTerm, 
    setSearchTerm,
    searchBy = 'appId',
    setSearchBy,
    onToggleFavorite,
    empId,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
    onRefresh,
    pageViewHandler
}) {
    const formatTime = (timeVal) => {
        if (!timeVal) return '';
        try {
            const date = new Date(timeVal);
            const now = new Date();
            if (date.toDateString() === now.toDateString()) {
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        } catch (e) {
            return '';
        }
    };

    return (
        <div className="chat-sidebar">
            {/* Header / Search / Filters */}
            <div className="sidebar-header">
                <div className="sidebar-top-bar">
                    <h3 className="sidebar-title">
                        {pageViewHandler && (
                            <button 
                                className="back-icon-btn" 
                                onClick={(e) => pageViewHandler(e, 'landingPage')}
                                title="Back to Dashboard"
                            >
                                <ArrowLeft size={18} color="#0d7a6e" style={{width:'auto',margin:'5px 0 0 0'}} />
                            </button>
                        )}
                        <MessageSquare size={22} color="#0d7a6e" />
                        Conversations
                    </h3>
                    {onRefresh && (
                        <button className="refresh-btn" onClick={onRefresh} title="Refresh Conversations">
                            <RefreshCcw size={15} color="#64748b" />
                        </button>
                    )}
                </div>

                <div className="assignee-badge">
                    <span>Assignee: <strong>{empId}</strong></span>
                </div>
                
                {setSearchBy && (
                    <div className="search-mode-row">
                        <button 
                            type="button" 
                            className={`search-mode-btn ${searchBy === 'appId' ? 'active' : ''}`}
                            onClick={() => setSearchBy('appId')}
                        >
                            App ID
                        </button>
                        <button 
                            type="button" 
                            className={`search-mode-btn ${searchBy === 'mobile' ? 'active' : ''}`}
                            onClick={() => setSearchBy('mobile')}
                        >
                            Mobile No
                        </button>
                    </div>
                )}

                <div className="search-wrapper">
                    <Search size={16} className="search-icon" style={{margin:'0'}} />
                    <input 
                        type={searchBy === 'mobile' ? 'tel' : 'text'} 
                        placeholder={searchBy === 'mobile' ? 'Search Mobile Number...' : 'Search Application ID...'} 
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                            ×
                        </button>
                    )}
                </div>

                <div className="filter-row">
                    <button 
                        className={`filter-pill ${filterType === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterType('all')}
                    >
                        All
                    </button>
                    <button 
                        className={`filter-pill ${filterType === 'unread' ? 'active' : ''}`}
                        onClick={() => setFilterType('unread')}
                    >
                        Unread
                    </button>
                    <button 
                        className={`filter-pill ${filterType === 'starred' ? 'active' : ''}`}
                        onClick={() => setFilterType('starred')}
                    >
                        Starred
                    </button>
                </div>
            </div>

            {/* Conversations List */}
            <div className="conversations-list">
                {conversations.length > 0 ? (
                    <>
                        {conversations.map((conv) => {
                            const isSelected = activeChat && (activeChat.applicationId === conv.applicationId || (activeChat.userId && activeChat.userId === conv.userId));
                            const customerName = conv.name || conv.userName || conv.customerName;
                            const initials = getInitials(customerName, conv.applicationId);
                            const isFavorite = conv.favorite === true || conv.isFavorite === true;
                            const isUnread = !isSelected && (conv.unreadCount > 0 || conv.read === false);

                            return (
                                <div 
                                    key={conv.applicationId || conv.userId} 
                                    className={`conv-item ${isSelected ? 'active' : ''}`}
                                    onClick={() => onSelectChat(conv)}
                                >
                                    <div className="avatar">
                                        {initials}
                                    </div>
                                    <div className="conv-details">
                                        <div className="conv-row-1">
                                            <span className="customer-name" title={customerName || conv.applicationId}>
                                                {customerName || `App #${conv.applicationId}`}
                                            </span>
                                            <span className="message-time">
                                                {formatTime(conv.addedOn || conv.createdAt || conv.timestamp)}
                                            </span>
                                        </div>
                                        <div className="conv-row-sub">
                                            <span className="app-id-tag">
                                                App ID: <strong>{conv.applicationId}</strong>
                                            </span>
                                        </div>
                                        <div className="conv-row-2">
                                            <span className="message-snippet" title={conv.latestMessage || conv.comment}>
                                                {conv.latestMessage || conv.comment || 'No messages yet'}
                                            </span>
                                            <div className="meta-badges">
                                                {isUnread && (
                                                    <span className="unread-badge">
                                                        {conv.unreadCount > 0 ? conv.unreadCount : '1'}
                                                    </span>
                                                )}
                                                <button 
                                                    className={`star-btn ${isFavorite ? 'starred' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onToggleFavorite(conv.applicationId);
                                                    }}
                                                    title="Toggle Favorite (API #6)"
                                                >
                                                    <Star size={14} fill={isFavorite ? '#f59e0b' : 'none'} color={isFavorite ? '#f59e0b' : '#cbd5e1'} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {hasMore && (
                            <div className="load-more-wrapper">
                                <button className="load-more-btn" onClick={onLoadMore} disabled={loadingMore}>
                                    {loadingMore ? 'Loading...' : 'Load More Conversations'}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="no-chats">
                        <MessageSquare size={36} className="no-chats-icon" />
                        <div>No conversations found</div>
                    </div>
                )}
            </div>
        </div>
    );
}
